import { useState } from "react";
import Modal from "~/components/ui/Modal";
import { invoke } from "~/runtime";
import type { HTMLWidget, ImageWidget } from "~/types/widgets";

export interface Props {
  /**
   * @title Texto em cima do formulário
   */
  text: HTMLWidget;
  /**
   * @title Imagem de fundo
   */
  background: ImageWidget;
  /**
   * @title Link das recomendações
   * @description URL do banner "Enquanto isso, confira nossas recomendações para você" no modal de sucesso
   * @format uri
   */
  recommendationsHref?: string;
}

const ERROR_MSG = "Por favor, verifique este campo";
const PRIVACY_HREF = "/institucionais/antescompra/politicaprivacidade";
const MIN_CHARS = 3;

type FieldState = "default" | "error" | "valid";
type Validator = (value: string) => boolean;

const isEmailValid: Validator = (value) => /^\S+@\S+\.\S+$/.test(value);
const hasMinChars: Validator = (value) => value.trim().length >= MIN_CHARS;

function getFieldState(value: string, hasError: boolean, isValid: Validator): FieldState {
  if (hasError) return "error";
  if (value.trim() && isValid(value)) return "valid";
  return "default";
}

function labelClass(state: FieldState) {
  return state === "error" ? "text-[#DC2626]" : "text-white";
}

function inputClass(state: FieldState, extra = "") {
  const base =
    "w-full bg-white p-[10px] border border-solid outline-none placeholder:text-[#9CA3AF]";
  const byState = {
    default: "text-[#595959] border-[#CCC]",
    error: "text-[#DC2626] border-[#DC2626]",
    valid: "text-[#595959] border-[#CCC] pl-10",
  }[state];

  return `${base} ${byState} ${extra}`.trim();
}

function CheckIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M9 15.75C9.88642 15.75 10.7642 15.5754 11.5831 15.2362C12.4021 14.897 13.1462 14.3998 13.773 13.773C14.3998 13.1462 14.897 12.4021 15.2362 11.5831C15.5754 10.7642 15.75 9.88642 15.75 9C15.75 8.11358 15.5754 7.23583 15.2362 6.41689C14.897 5.59794 14.3998 4.85382 13.773 4.22703C13.1462 3.60023 12.4021 3.10303 11.5831 2.76381C10.7642 2.42459 9.88642 2.25 9 2.25C7.20979 2.25 5.4929 2.96116 4.22703 4.22703C2.96116 5.4929 2.25 7.20979 2.25 9C2.25 10.7902 2.96116 12.5071 4.22703 13.773C5.4929 15.0388 7.20979 15.75 9 15.75ZM8.826 11.73L12.576 7.23L11.424 6.27L8.199 10.1392L6.53025 8.46975L5.46975 9.53025L7.71975 11.7802L8.30025 12.3608L8.826 11.73Z"
        fill="#6AD954"
      />
    </svg>
  );
}

function SuccessModalIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="48"
      height="48"
      viewBox="0 0 48 48"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M24 44C26.6269 44.0033 29.2286 43.4874 31.6555 42.4821C34.0825 41.4768 36.2868 40.0019 38.142 38.142C40.0019 36.2868 41.4768 34.0825 42.4821 31.6555C43.4874 29.2286 44.0033 26.6269 44 24C44.0033 21.3731 43.4874 18.7714 42.4821 16.3445C41.4768 13.9176 40.0019 11.7132 38.142 9.85802C36.2868 7.99818 34.0825 6.52323 31.6555 5.51793C29.2286 4.51263 26.6269 3.99678 24 4.00002C21.3731 3.99678 18.7714 4.51263 16.3445 5.51793C13.9176 6.52323 11.7132 7.99818 9.85802 9.85802C7.99818 11.7132 6.52323 13.9176 5.51793 16.3445C4.51263 18.7714 3.99678 21.3731 4.00002 24C3.99678 26.6269 4.51263 29.2286 5.51793 31.6555C6.52323 34.0825 7.99818 36.2868 9.85802 38.142C11.7132 40.0019 13.9176 41.4768 16.3445 42.4821C18.7714 43.4874 21.3731 44.0033 24 44Z"
        stroke="#6AD954"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <path
        d="M16 24L22 30L34 18"
        stroke="#6AD954"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function BagIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="36"
      height="36"
      viewBox="0 0 36 36"
      fill="none"
      aria-hidden="true"
      className="shrink-0"
    >
      <path
        d="M28.0273 7.03125H24.5117V6.1875C24.5117 4.5 23.9082 3.04688 22.7012 1.82812C21.4941 0.609375 20.0352 0 18.3242 0C16.6133 0 15.1543 0.609375 13.9473 1.82812C12.7402 3.04688 12.1367 4.5 12.1367 6.1875V7.03125H8.26953L5 33.2578V33.3633C5 34.0898 5.25781 34.7109 5.77344 35.2266C6.28906 35.7422 6.91016 36 7.63672 36H29.082C29.7852 36 30.4004 35.7422 30.9277 35.2266C31.4551 34.7109 31.7188 34.0898 31.7188 33.3633L28.0273 7.03125ZM13.6836 6.1875C13.6836 4.89844 14.1348 3.80273 15.0371 2.90039C15.9395 1.99805 17.0352 1.54688 18.3242 1.54688C19.6133 1.54688 20.709 1.99805 21.6113 2.90039C22.5137 3.80273 22.9648 4.89844 22.9648 6.1875V7.03125H13.6836V6.1875ZM29.082 34.4531H7.63672C7.35547 34.4531 7.10938 34.3535 6.89844 34.1543C6.6875 33.9551 6.57031 33.7148 6.54688 33.4336L9.64062 8.61328H12.1367V10.8633H13.6836V8.57812H22.9648V10.8633H24.5117V8.57812H26.6914L30.1016 33.3633C30.1016 33.668 30.002 33.9199 29.8027 34.1191C29.6035 34.3184 29.3633 34.4297 29.082 34.4531ZM18.8867 20.3555L19.625 19.5117C20.0703 18.9727 20.5684 18.4863 21.1191 18.0527C21.6699 17.6191 22.25 17.25 22.8594 16.9453C23.2344 16.7578 23.6211 16.6406 24.0195 16.5938C24.418 16.5469 24.8047 16.582 25.1797 16.6992C25.6016 16.793 25.9531 17.0039 26.2344 17.332C26.5156 17.6602 26.668 18.0469 26.6914 18.4922C26.7617 19.8516 26.6504 21.1582 26.3574 22.4121C26.0645 23.666 25.4844 24.8438 24.6172 25.9453C24.3125 26.3438 23.9727 26.7246 23.5977 27.0879C23.2227 27.4512 22.8242 27.7852 22.4023 28.0898L22.6484 27.668L22.9648 27.2812C23.3867 26.6719 23.7617 26.0449 24.0898 25.4004C24.418 24.7559 24.6875 24.0938 24.8984 23.4141C24.9688 23.0859 25.0098 22.7695 25.0215 22.4648C25.0332 22.1602 25.0039 21.8555 24.9336 21.5508C24.8867 21.1758 24.7227 20.8828 24.4414 20.6719C24.1602 20.4609 23.8438 20.3672 23.4922 20.3906C23.4453 20.3906 23.3926 20.4023 23.334 20.4258C23.2754 20.4492 23.2109 20.4727 23.1406 20.4961C22.6953 20.6367 22.2793 20.8242 21.8926 21.0586C21.5059 21.293 21.1602 21.5742 20.8555 21.9023C20.4805 22.3242 20.1172 22.7461 19.7656 23.168C19.4141 23.5898 19.0742 24.0117 18.7461 24.4336L18.5703 24.5742C18.5 24.4336 18.4355 24.2871 18.377 24.1348C18.3184 23.9824 18.2539 23.8359 18.1836 23.6953C17.8555 22.9688 17.4219 22.3301 16.8828 21.7793C16.3438 21.2285 15.7227 20.7656 15.0195 20.3906C14.5977 20.1797 14.1992 20.0977 13.8242 20.1445C13.4492 20.1914 13.1445 20.3438 12.9102 20.6016C12.7227 20.8359 12.5645 21.082 12.4355 21.3398C12.3066 21.5977 12.2188 21.8672 12.1719 22.1484C12.0781 22.8047 12.1016 23.4434 12.2422 24.0645C12.3828 24.6855 12.6172 25.2773 12.9453 25.8398C13.3906 26.6133 13.8711 27.3457 14.3867 28.0371C14.9023 28.7285 15.4766 29.3906 16.1094 30.0234C16.2031 30.1172 16.3027 30.2051 16.4082 30.2871C16.5137 30.3691 16.6133 30.4688 16.707 30.5859L16.6367 30.6914C16.5195 30.6211 16.4023 30.5625 16.2852 30.5156C16.168 30.4688 16.0508 30.3984 15.9336 30.3047C14.6445 29.625 13.5312 28.7109 12.5938 27.5625C11.6562 26.4141 10.9883 25.125 10.5898 23.6953C10.4492 23.2031 10.3496 22.7168 10.291 22.2363C10.2324 21.7559 10.2266 21.2695 10.2734 20.7773C10.2734 20.332 10.3027 19.8867 10.3613 19.4414C10.4199 18.9961 10.5078 18.5625 10.625 18.1406C10.7656 17.7188 10.9473 17.332 11.1699 16.9805C11.3926 16.6289 11.6797 16.3242 12.0312 16.0664C12.5703 15.6445 13.1211 15.457 13.6836 15.5039C14.2461 15.5508 14.832 15.7031 15.4414 15.9609C16.0977 16.3125 16.6895 16.7695 17.2168 17.332C17.7441 17.8945 18.1602 18.5273 18.4648 19.2305C18.5117 19.418 18.5703 19.6055 18.6406 19.793C18.7109 19.9805 18.793 20.168 18.8867 20.3555Z"
        fill="#F0863F"
      />
    </svg>
  );
}

function ChevronIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
      className="shrink-0"
    >
      <path
        d="M6 3.5L10.5 8L6 12.5"
        stroke="#F0863F"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      aria-hidden="true"
    >
      <path d="M5 5L15 15M15 5L5 15" stroke="#9CA3AF" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

interface FormFieldProps {
  id: string;
  label: string;
  placeholder: string;
  state: FieldState;
  value: string;
  onChange: (value: string) => void;
  multiline?: boolean;
}

function FormField({
  id,
  label,
  placeholder,
  state,
  value,
  onChange,
  multiline = false,
}: FormFieldProps) {
  const isError = state === "error";
  const isValid = state === "valid";

  return (
    <div className="flex flex-col mb-4">
      <label
        className={`inline-block max-w-full mb-[5px] font-bold ${labelClass(state)}`}
        htmlFor={id}
      >
        {label}
      </label>
      <div className="relative">
        {isValid && (
          <span
            className={`absolute left-3 pointer-events-none ${
              multiline ? "top-3" : "top-1/2 -translate-y-1/2"
            }`}
          >
            <CheckIcon />
          </span>
        )}
        {multiline ? (
          <textarea
            id={id}
            name={id}
            className={inputClass(state, "h-24")}
            placeholder={placeholder}
            aria-label={placeholder}
            aria-invalid={isError}
            value={value}
            onChange={(e) => onChange(e.currentTarget.value)}
          />
        ) : (
          <input
            id={id}
            name={id}
            type={id === "email" ? "email" : "text"}
            className={inputClass(state)}
            placeholder={placeholder}
            aria-label={placeholder}
            aria-invalid={isError}
            value={value}
            onChange={(e) => onChange(e.currentTarget.value)}
          />
        )}
      </div>
      <span
        className={`block h-5 text-sm font-normal leading-5 text-[#DC2626] font-sans ${
          isError ? "" : "invisible"
        }`}
        aria-hidden={!isError}
      >
        {ERROR_MSG}
      </span>
    </div>
  );
}

interface SuccessModalProps {
  open: boolean;
  onClose: () => void;
  recommendationsHref?: string;
}

function SuccessModal({ open, onClose, recommendationsHref }: SuccessModalProps) {
  return (
    <Modal open={open} onClose={onClose} loading="eager" alt="Fechar modal de sucesso">
      <div className="modal-box relative max-w-[506px] w-[calc(100%-2rem)] rounded-none bg-white py-6 px-8 shadow-lg font-sans">
        <button
          type="button"
          className="absolute top-4 right-4 z-10 cursor-pointer"
          aria-label="Fechar"
          onClick={onClose}
        >
          <CloseIcon />
        </button>

        <div className="flex flex-col items-center lg:gap-6 gap-5">
          <SuccessModalIcon />

          <h2 className="text-center text-2xl font-bold leading-[120%] uppercase text-[#333]">
            Solicitação recebida!
          </h2>

          <div className="pb-5 lg:pb-6 border-b border-solid border-[#CCC] flex flex-col gap-3 lg:gap-4 text-left text-base font-normal leading-[120%] text-[#333]">
            <p>Recebemos sua solicitação e nossa equipe irá analisar o produto informado.</p>
            <p>
              Caso o item esteja disponível ou tenhamos alguma informação relevante, entraremos em
              contato através do e-mail informado.
            </p>
            <p>Obrigado por nos ajudar a melhorar sua experiência na loja!</p>
          </div>

          <div className="w-full flex flex-col lg:gap-6 gap-5">
            {recommendationsHref && (
              <a
                href={recommendationsHref}
                className="flex w-full items-center gap-3 bg-[#F8EDE8] p-4 text-left hover:opacity-90 min-h-[70px]"
              >
                <BagIcon />
                <span className="flex-1 text-base font-bold leading-[120%] text-[#333]">
                  Enquanto isso, confira nossas recomendações para você
                </span>
                <ChevronIcon />
              </a>
            )}

            <a
              href="/"
              className="flex w-full h-[52px] items-center justify-center bg-[#DC2626] hover:opacity-90 px-4 py-3 text-center text-lg font-bold leading-7 uppercase text-white rounded-none"
            >
              Continuar navegando
            </a>
          </div>
        </div>
      </div>
    </Modal>
  );
}

function NotFound({ text, background, recommendationsHref }: Props) {
  const [nameValue, setNameValue] = useState("");
  const [emailValue, setEmailValue] = useState("");
  const [messageValue, setMessageValue] = useState("");

  const [nameError, setNameError] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [messageError, setMessageError] = useState(false);

  const [submitted, setSubmitted] = useState(false);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const nameState = getFieldState(nameValue, nameError, hasMinChars);
  const emailState = getFieldState(emailValue, emailError, isEmailValid);
  const messageState = getFieldState(messageValue, messageError, hasMinChars);

  const buttonBlocked = nameError || emailError || messageError || loading;

  const updateField = (
    setValue: (value: string) => void,
    error: boolean,
    setError: (value: boolean) => void,
    isValid: Validator,
    value: string,
  ) => {
    setValue(value);
    if (submitted || error) {
      setError(!isValid(value));
    }
  };

  const validateAll = () => {
    const nameOk = hasMinChars(nameValue);
    const emailOk = isEmailValid(emailValue);
    const messageOk = hasMinChars(messageValue);

    setNameError(!nameOk);
    setEmailError(!emailOk);
    setMessageError(!messageOk);

    return nameOk && emailOk && messageOk;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (buttonBlocked) return;

    setSubmitted(true);
    if (!validateAll()) return;

    setLoading(true);

    try {
      const result = await invoke.site.actions.buscaVazia({
        nome: nameValue.trim(),
        email: emailValue.trim(),
        mensagem: messageValue.trim(),
      });

      if (!result?.Id && !result?.DocumentId) {
        console.error("buscaVazia: resposta sem Id", result);
        return;
      }

      setSuccess(true);
    } catch (error) {
      console.error("buscaVazia: falha ao enviar", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full flex justify-center items-center">
      <div
        className="bg-cover float-left p-0 text-center w-full lg:mb-8 min-h-[500px]"
        style={{ background: `url(${background}) 50% no-repeat #000`, backgroundSize: "cover" }}
      >
        <div className="mt-5 lg:mt-14 mx-auto lg:ml-[30px]" style={{ maxWidth: "620px" }}>
          <div className="flex flex-col px-10 lg:pl-5 lg:pr-0 text-start">
            <div
              dangerouslySetInnerHTML={{ __html: text }}
              className="flex flex-col gap-5 max-w-[620px] mb-10 text-sm text-white max-lg:[&_p]:text-center max-lg:[&_h1]:text-center [&_h1]:text-xl lg:[&_h1]:text-4xl [&_h1]:font-bold [&_p]:leading-normal [&_ul]:pl-4 [&_li]:list-disc [&_li]:leading-6"
            />

            <form
              className="bg-transparent mb-[80px] p-0"
              onSubmit={handleSubmit}
              noValidate
              autoComplete="off"
            >
              <FormField
                id="name"
                label="Nome (*)"
                placeholder="Nome"
                state={nameState}
                value={nameValue}
                onChange={(value) =>
                  updateField(setNameValue, nameError, setNameError, hasMinChars, value)
                }
              />

              <FormField
                id="email"
                label="Email (*)"
                placeholder="E-mail"
                state={emailState}
                value={emailValue}
                onChange={(value) =>
                  updateField(setEmailValue, emailError, setEmailError, isEmailValid, value)
                }
              />

              <div className="flex flex-col mb-4">
                <label
                  className="inline-block text-white max-w-full mb-[5px] font-bold"
                  htmlFor="order"
                >
                  Número do Pedido
                </label>
                <input
                  type="text"
                  className={inputClass("default", "mb-5")}
                  id="order"
                  name="order"
                  placeholder="Número do Pedido"
                  aria-label="Número do Pedido"
                />
              </div>

              <FormField
                id="message"
                label="Mensagem (*)"
                placeholder="Deixe sua mensagem..."
                state={messageState}
                value={messageValue}
                multiline
                onChange={(value) =>
                  updateField(setMessageValue, messageError, setMessageError, hasMinChars, value)
                }
              />

              <p className="mb-5 text-sm font-normal leading-6 text-white/80">
                Ao enviar você concorda com nossos termos de{" "}
                <a
                  href={PRIVACY_HREF}
                  className="underline hover:opacity-80"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Política de Privacidade
                </a>
              </p>

              <button
                type="submit"
                disabled={buttonBlocked}
                className={`bg-red-600 text-white text-lg font-bold py-3 text-center uppercase w-full flex items-center justify-center min-h-[52px] ${
                  buttonBlocked
                    ? "opacity-50 cursor-not-allowed"
                    : "hover:opacity-80 cursor-pointer"
                }`}
              >
                {loading ? <span className="loading loading-spinner" /> : "Enviar"}
              </button>
            </form>
          </div>
        </div>
      </div>

      <SuccessModal
        open={success}
        onClose={() => setSuccess(false)}
        recommendationsHref={recommendationsHref}
      />
    </div>
  );
}

export default NotFound;
